import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Clock3, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const QUICK_DELAYS = [10, 15, 20, 30];

function createRequestKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `delay-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function shortTime(value) {
  return String(value || '').slice(0, 5);
}

export default function AppointmentOverrunPrompt({ appointmentId, encounterId, onApplied }) {
  const [warning, setWarning] = useState(null);
  const [step, setStep] = useState('warning');
  const [customMinutes, setCustomMinutes] = useState('');
  const [preview, setPreview] = useState(null);
  const [selectedMinutes, setSelectedMinutes] = useState(null);
  const [loading, setLoading] = useState(false);
  const hiddenWarningKeys = useRef(new Set());
  const requestKey = useRef(null);

  const closeLocally = useCallback(() => {
    if (warning?.warningKey) hiddenWarningKeys.current.add(warning.warningKey);
    setWarning(null);
    setPreview(null);
    setSelectedMinutes(null);
    setCustomMinutes('');
    setStep('warning');
    requestKey.current = null;
  }, [warning]);

  const loadWarning = useCallback(async () => {
    if (!appointmentId || !encounterId) return;
    try {
      const { data } = await api.get('/dashboard/appointment-overrun/warning', {
        params: { appointmentId, encounterId },
      });
      if (data?.active && !hiddenWarningKeys.current.has(data.warningKey)) {
        setWarning(data);
      } else if (!data?.active) {
        setWarning(null);
      }
    } catch (_error) {
      // The Prescription Pad must remain usable if the non-blocking timing monitor is unavailable.
    }
  }, [appointmentId, encounterId]);

  useEffect(() => {
    loadWarning();
    const interval = window.setInterval(loadWarning, 30000);
    return () => window.clearInterval(interval);
  }, [loadWarning]);

  const dismissOnTime = async () => {
    setLoading(true);
    try {
      await api.post('/dashboard/appointment-overrun/dismiss', {
        appointmentId,
        encounterId,
        warningKey: warning.warningKey,
      });
      closeLocally();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Unable to dismiss timing warning');
    } finally {
      setLoading(false);
    }
  };

  const requestPreview = async (minutes) => {
    const parsed = Number(minutes);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 180) {
      toast.error('Enter additional time between 1 and 180 minutes');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/dashboard/appointment-overrun/preview', {
        appointmentId,
        encounterId,
        delayMinutes: parsed,
      });
      setSelectedMinutes(parsed);
      setPreview(data);
      setStep('preview');
      requestKey.current = createRequestKey();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Unable to calculate the delay impact');
    } finally {
      setLoading(false);
    }
  };

  const applyAndNotify = async () => {
    if (!preview?.canApply || !requestKey.current) return;
    setLoading(true);
    try {
      const { data } = await api.post('/dashboard/appointment-overrun/apply', {
        appointmentId,
        encounterId,
        delayMinutes: selectedMinutes,
        previewFingerprint: preview.previewFingerprint,
        idempotencyKey: requestKey.current,
      }, {
        headers: { 'Idempotency-Key': requestKey.current },
      });
      toast.success(`${data.affectedAppointments?.length || 0} appointment${data.affectedAppointments?.length === 1 ? '' : 's'} updated and notification state recorded`);
      onApplied?.(data);
      closeLocally();
    } catch (error) {
      const message = error.response?.data?.error || 'Unable to apply the approved delay';
      toast.error(message);
      if (error.response?.status === 409) setStep('duration');
    } finally {
      setLoading(false);
    }
  };

  if (!warning) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="appointment-overrun-title"
        className="w-full max-w-xl overflow-hidden rounded-[28px] border border-amber-100 bg-white shadow-[0_32px_100px_-40px_rgba(15,23,42,0.7)]"
      >
        <div className="flex items-start justify-between gap-4 border-b border-amber-100 bg-amber-50 px-5 py-4 sm:px-6">
          <div className="flex gap-3">
            <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
              <Clock3 className="h-5 w-5" />
            </span>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">Schedule check</p>
              <h2 id="appointment-overrun-title" className="mt-1 text-xl font-semibold text-slate-900">
                {step === 'warning' ? `Next appointment in ${warning.minutesRemaining} minute${warning.minutesRemaining === 1 ? '' : 's'}` : step === 'duration' ? 'How much additional time?' : 'Review delay impact'}
              </h2>
            </div>
          </div>
          <button type="button" onClick={closeLocally} aria-label="Close timing warning" className="rounded-full p-2 text-slate-500 hover:bg-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 sm:p-6">
          {step === 'warning' ? (
            <>
              <p className="text-base font-semibold text-slate-900">
                Next patient: {warning.nextAppointment?.patientName} at {warning.nextAppointment?.timeLabel || shortTime(warning.nextAppointment?.time)}
              </p>
              <p className="mt-2 text-sm text-slate-600">
                {warning.minutesRemaining === 1 ? '1 minute remaining' : `${warning.minutesRemaining} minutes remaining`}. Are you on time?
              </p>
              {warning.nextAppointment?.checkedIn ? (
                <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
                  This patient is already checked in. Reception action will be required for any delay.
                </p>
              ) : null}
              <div className="mt-6 grid gap-2 sm:grid-cols-2">
                <button type="button" onClick={dismissOnTime} disabled={loading} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  YES
                </button>
                <button type="button" onClick={() => setStep('duration')} disabled={loading} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60">
                  <AlertTriangle className="h-4 w-4" />
                  NEED MORE TIME
                </button>
              </div>
            </>
          ) : null}

          {step === 'duration' ? (
            <>
              <p className="text-sm text-slate-600">No appointment changes until you review and approve the impact.</p>
              <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {QUICK_DELAYS.map((minutes) => (
                  <button key={minutes} type="button" disabled={loading} onClick={() => requestPreview(minutes)} className="min-h-11 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:border-amber-300 hover:bg-amber-50 disabled:opacity-60">
                    +{minutes} min
                  </button>
                ))}
              </div>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <label className="flex-1">
                  <span className="sr-only">Custom delay minutes</span>
                  <input type="number" min="1" max="180" value={customMinutes} onChange={(event) => setCustomMinutes(event.target.value)} placeholder="Custom minutes" className="h-11 w-full rounded-2xl border border-slate-200 px-4 text-sm outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-50" />
                </label>
                <button type="button" disabled={loading} onClick={() => requestPreview(customMinutes)} className="min-h-11 rounded-full bg-slate-900 px-5 text-sm font-semibold text-white disabled:opacity-60">
                  {loading ? 'Calculating…' : 'Preview'}
                </button>
              </div>
              <button type="button" onClick={closeLocally} className="mt-5 text-sm font-semibold text-slate-500 hover:text-slate-800">Cancel</button>
            </>
          ) : null}

          {step === 'preview' && preview ? (
            <>
              <p className="text-sm font-medium text-slate-700">Adding {selectedMinutes} minutes will affect:</p>
              <div className="mt-4 max-h-64 space-y-2 overflow-y-auto">
                {preview.affectedAppointments?.map((item) => (
                  <div key={item.appointmentId} className="flex flex-col gap-1 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm font-semibold text-slate-900">{item.patientName}</span>
                    <span className="text-sm font-medium tabular-nums text-slate-600">{shortTime(item.oldTime)} → {shortTime(item.newTime)}</span>
                  </div>
                ))}
                {preview.protectedAppointments?.map((item) => (
                  <div key={item.appointmentId} className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                    <p className="text-sm font-semibold text-amber-900">{item.patientName} — {shortTime(item.oldTime)}</p>
                    <p className="mt-1 text-xs font-medium text-amber-800">{item.message}</p>
                  </div>
                ))}
                {preview.conflicts?.map((item, index) => (
                  <div key={`${item.code}-${index}`} className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800">{item.message}</div>
                ))}
                {!preview.affectedAppointments?.length && !preview.protectedAppointments?.length && !preview.conflicts?.length ? (
                  <p className="rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-500">No downstream appointment needs to move.</p>
                ) : null}
              </div>
              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button type="button" onClick={closeLocally} disabled={loading} className="min-h-11 rounded-full border border-slate-200 px-5 text-sm font-semibold text-slate-600 disabled:opacity-60">Cancel</button>
                <button type="button" onClick={applyAndNotify} disabled={loading || !preview.canApply} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-indigo-600 px-5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Apply &amp; Notify
                </button>
              </div>
              {!preview.canApply ? <p className="mt-3 text-xs font-medium text-rose-700">Resolve the protected patient or scheduling conflict before applying this delay.</p> : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
